import axios from 'axios';
import { MDBFileUpload } from 'mdb-react-file-upload';
import { MDBAlert, MDBBtn, MDBCol, MDBContainer, MDBRow, MDBSpinner } from 'mdb-react-ui-kit';
import { useSnackbar } from 'notistack';
import PropTypes from 'prop-types';
import React, { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { CategoryContext } from '../context/CategoryContext';
import { DomainContext } from '../context/DomainProvider';
import { GeographyContext } from '../context/GeographyProvider';
import { MiscContext } from '../context/MiscProvider';
import { NavbarContext } from '../context/NavProvider';
import { UserProfileContext } from '../context/UserProfileProvider';
import { getDomainsAndCategories } from '../utils/Common';
import AddNewEntity from './AddNewEntity';

function UploadEntities({ setItemAdded, handleIsEntityCreateModalOpenToggle, domain, onDomainsUpdate, enableGoToInventory, fetchItems, getCatgs, toggleItemAdded }) {
  const navigate = useNavigate();
  const { domainId } = useParams();
  const { pageData } = useContext(MiscContext);
  const { selectDomains, setSelectDomains, setGroupedDomainsByDomain, setDomainImages } = useContext(DomainContext);
  const { setGroupedCategoriesByDomain, setCategoryImages,selectCategories, setSelectCategories } = useContext(CategoryContext);
  const {  setBusiness } = useContext(NavbarContext);
  const { ipAddress, setCountry } = useContext(GeographyContext);
  const { tokenLoaded } = useContext(UserProfileContext);
  const baseUrl = process.env.REACT_APP_INVENTORY_URL;
  const { enqueueSnackbar } = useSnackbar();

  const [itemImageMap, setItemImageMap] = useState(new Map());
  const [addNewModalOpen, setAddNewModalOpen] = useState(false);
  const [isUploadDisabled, setIsUploadDisabled] = useState(true);
  const [uploadStatus, setUploadStatus] = useState('idle');
  const [files, setFiles] = useState([]);
  const [isDuplicate, setIsDuplicate] = useState(false);
  const [alertMessage, setAlertMessage] = useState('');
  const [fileKey, setFileKey] = useState(Date.now()); // Unique key to force re-render
  const [uploadComplete, setUploadComplete] = useState(false);
  const [alertVisible, setAlertVisible] = useState(false);

  let location = useLocation();
  let { selectCategories1, selectDomains1 } = location.state || {};

  useEffect(() => {
    if (tokenLoaded && selectDomains?.length === 0) {
      getDomainsAndCategories(domainId, location, ipAddress, tokenLoaded, setBusiness, setCountry, selectDomains, setSelectCategories, setSelectDomains, setGroupedCategoriesByDomain, setCategoryImages, setGroupedDomainsByDomain, setDomainImages);
    }
  }, [domainId,  tokenLoaded,  setSelectCategories, setSelectDomains,  setCategoryImages, setDomainImages]);
 
  useEffect(() => {
    setIsUploadDisabled(files?.length === 0 || isDuplicate);
  }, [files, isDuplicate]);



  const handleRemoveFile = useCallback((file) => {
    if (!file) return; // or throw an error, depending on your use case
    const updatedFiles = files.filter((f) => f && f.name !== file.name);
    setFiles(updatedFiles);
    setFileKey(Date.now());
  }, [files]);


  const handleFilesChange = useCallback((e) => {
    const selectedFiles = Array.from(e); // Ensure selectedFiles is an array
    if (selectedFiles.length > 0) {
      const uniqueSelectedFiles = selectedFiles.filter((selectedFile) => !files.some((existingFile) => existingFile && existingFile.name === selectedFile.name));
      if (uniqueSelectedFiles.length === 0) {
        setIsDuplicate(true);
        setAlertMessage("You cannot upload duplicate files.");
      } else {
        setFiles((prevFiles) => [...prevFiles, ...uniqueSelectedFiles]);
      }
    }
  }, [files]);

  const toggleUploadAlert = useCallback(() => {
    setTimeout(() => {
      setUploadComplete(false);
    }, 3000);
    setUploadComplete(true);
  }, []);

  const toggleAlertVisible = useCallback(() => {
    setAlertVisible(!alertVisible);
  }, [alertVisible]);

  const handleUpload = useCallback(async () => {
    const formData = new FormData();
    let hasCSV = false;

    files.forEach((file) => {
      if (file?.name?.toLowerCase().endsWith('.csv')) {
        formData.append('csv', file);
        hasCSV = true;
      } else {
        formData.append('images', file);
      }
    });

    if (!hasCSV) {
      setAlertVisible(true);
      setAlertMessage('Please attach a CSV file.');
      return;
    }
    setUploadStatus('inProgress');

    try {
      const url = `${baseUrl}/upload/files/domain/${domainId}`;
      const response = await axios.post(url, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
        withCredentials: true,
      });

      if (response.status === 200 || response.status === 201) {
        setFiles([]);
        setIsUploadDisabled(false);
        setItemImageMap(new Map(Object.entries(response.data)));
        setUploadComplete(true);
        toggleUploadAlert();
        setUploadStatus('done');
        fetchItems && fetchItems(domainId);
        setAlertMessage('Upload success');
      } else {
        setIsUploadDisabled(false);
        setFiles([]);
        alert(response.data);
        setUploadStatus('done');
      }
    } catch (error) {
      setIsUploadDisabled(false);
      setFiles([]);
      if (error.response && error.response.data) {
        const { message, status, code } = error.response.data;
        enqueueSnackbar(`${status} ${code}: ${message}`, { variant: 'error' });
      } else {
        enqueueSnackbar('Unexpected error occurred', { variant: 'error' });
      }
      setUploadStatus('done');
      setAlertVisible(true);
      if (error.response && error.response.data) {
        setAlertMessage(error.response.data.message);
      }
    }
  }, [files, baseUrl, domainId, enqueueSnackbar, fetchItems, toggleUploadAlert]);

  const alertProperties = useMemo(() => {
    if (uploadComplete) {
      return {
        show: true,
        className: 'm-1',
        color: 'success',
        onClick: toggleUploadAlert,
      };
    } else if (alertVisible) {
      return {
        show: true,
        className: 'm-1',
        color: 'success',
        onClick: toggleAlertVisible,
      };
    }
    return {};
  }, [uploadComplete, alertVisible, toggleUploadAlert, toggleAlertVisible]);
  const toggleAddNewModal = () => {
    setAddNewModalOpen && setAddNewModalOpen(!addNewModalOpen);
  };
  return (
    <section>
      {(alertProperties.show || uploadComplete)? 
        <MDBAlert open={alertProperties.show} style={{ position: 'fixed', bottom: '20px', right: '20px', zIndex: 9999, width: 'auto', maxWidth: '90%' }}>
          <i className='fas fa-check-circle me-3'></i>{alertMessage}
          <MDBBtn color='link' rippleColor='dark' onClick={alertProperties.onClick}>close</MDBBtn>
        </MDBAlert>
       : isDuplicate &&
        <MDBAlert open={isDuplicate} className='w-10' color='info' autohide onClose={() => { setIsDuplicate(false); }} delay={10000}>
          {alertMessage}
          <MDBBtn color='link' rippleColor='dark' onClick={() => setIsDuplicate(false)}>close</MDBBtn>
        </MDBAlert>
      }
        <MDBContainer className='py-5'>
          <MDBAlert open className='w-100' color='warning'>
            <h4 className='alert-heading'>Well done!</h4>
            <p>
            {pageData?.bulkUploadMessages?.map((message, mId) => (
                           <li key={mId}>{`${message}`}</li>
                         ))}
            </p>
            <p>Please make sure there are no spaces between to header elemnts :
              <h6> NOT VALID:  &nbsp;&nbsp; &nbsp;&nbsp;  Item, &nbsp;&nbsp;&nbsp;&nbsp;    Category,   &nbsp;&nbsp; &nbsp;&nbsp; ImageFile, &nbsp;&nbsp;&nbsp;&nbsp;   Subdomain <br />
                VALID: &nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp; &nbsp;&nbsp; &nbsp;&nbsp;   Item,Category,ImageFile,Subdomain <br />
                the contents CAN be spaced
              </h6>
            </p>
            <table style={{ textAlign: 'center' }}>
              <thead style={{ textAlign: 'center' }}>
                <tr style={{ textAlign: 'center' }}>
                  <th style={{ textAlign: 'center' }}>Item </th>
                  <th style={{ textAlign: 'center' }}>Category</th>
                  <th style={{ textAlign: 'center' }}>ImageFile</th>
                  <th style={{ textAlign: 'center' }}>Subdomain</th>
                  <th style={{ textAlign: 'center' }}>isselling</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td style={{ textAlign: 'center' }}>item1,&nbsp;&nbsp;</td>
                  <td style={{ textAlign: 'center' }}>category1,</td>
                  <td style={{ textAlign: 'center' }}>image1.png,</td>
                  <td style={{ textAlign: 'center' }}>edina</td>
                  <td style={{ textAlign: 'center' }}>0</td>
                </tr>
                <tr>
                  <td style={{ textAlign: 'center' }}>item2,&nbsp;&nbsp;</td>
                  <td style={{ textAlign: 'center' }}>category2,</td>
                  <td style={{ textAlign: 'center' }}>image2.png,</td>
                  <td style={{ textAlign: 'center' }}>plymouth</td>
                  <td style={{ textAlign: 'center' }}>1</td>
                </tr>
                <tr>
                  <td style={{ textAlign: 'center' }}>item3,&nbsp;&nbsp;</td>
                  <td style={{ textAlign: 'center' }}>category3,</td>
                  <td style={{ textAlign: 'center' }}>image3.png,</td>
                  <td style={{ textAlign: 'center' }}>edina</td>
                  <td style={{ textAlign: 'center' }}>0</td>
                </tr>
              </tbody>
            </table>
          </MDBAlert>

          <MDBRow className='justify-content-center'>
            <MDBCol md='6'>
              <MDBFileUpload
                id="fileInput1"
                key={fileKey}
                maxFileSize='2M'
                multiple
                maxFileQuantity={10}
                acceptedExtensions={['image/png', 'image/jpeg', 'image/csv', 'image/pdf']}
                defaultMessage={<h4 className='alert-heading'>Upload a CSV and image files with the above format</h4>}
                color='success'
                onChange={(files) => handleFilesChange(files)}
                onRemove={(file) => handleRemoveFile(file)}
              />
              {isDuplicate && <div className="alert alert-danger">{alertMessage}</div>}
            </MDBCol>
          </MDBRow>

          <MDBRow className='justify-content-center'>
            <MDBCol md='auto'>
              <div className="d-flex">
                {uploadStatus === 'inProgress' ? (
                  <div className='text-center'>
                    <MDBSpinner color='dark'>
                      <span className='visually-hidden'>Loading...</span>
                    </MDBSpinner>
                  </div>
                ) : (
                  <MDBBtn color='dark' onClick={() => { navigate('/domain/entities'); handleIsEntityCreateModalOpenToggle() }} style={{ marginTop: '12px', marginRight: '12px' }} disabled={!enableGoToInventory || !uploadComplete}>
                    Go to Inventory
                  </MDBBtn>
                )}

                <MDBBtn color='dark' onClick={handleUpload} style={{ marginTop: '12px' }} disabled={isUploadDisabled}>
                  Upload
                </MDBBtn>

                <MDBBtn color='success' style={{ marginTop: '12px', marginLeft: '12px' }} onClick={toggleAddNewModal}>
                  Add New Item
                </MDBBtn>
              </div>
            </MDBCol>
          </MDBRow>

        </MDBContainer>
      
      <AddNewEntity
        key={`AddNewEntity-${addNewModalOpen}`}
        show={addNewModalOpen}
        handleAddModalClose={toggleAddNewModal}
        setAddNewModalOpen={setAddNewModalOpen}
        baseUrl={baseUrl}
        onItemAdded={setItemAdded}
        toggleAlertVisible={toggleAlertVisible}
        setAlertMessage={setAlertMessage}
        setAlertVisible={setAlertVisible}
        handleIsEntityCreateModalOpenToggle={handleIsEntityCreateModalOpenToggle}
        selectDomains={selectDomains || selectDomains1}
        selectCategories={selectCategories || selectCategories1}
        setUploadComplete={setUploadComplete}
        onDomainsUpdate={onDomainsUpdate}
        domain={domain}
        toggleItemAdded={toggleItemAdded}
      />

      {handleIsEntityCreateModalOpenToggle &&
        <div className='d-flex'>
          <MDBBtn className='ms-auto' size='sm' color='dark' onClick={handleIsEntityCreateModalOpenToggle}>x</MDBBtn>
        </div>
      }
    </section>
  );
}

UploadEntities.propTypes = {
  setItemAdded: PropTypes.func,
  handleIsEntityCreateModalOpenToggle: PropTypes.func,
  domain: PropTypes.object,
  onDomainsUpdate: PropTypes.func,
  enableGoToInventory: PropTypes.bool,
  fetchItems: PropTypes.func,
  getCatgs: PropTypes.func,
  toggleItemAdded: PropTypes.func,
};

export default UploadEntities;
